import React, { useState } from 'react'
import { MoreHoriz } from '@mui/icons-material'
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  SxProps,
  Theme,
  Typography
} from '@mui/material'

// Shared look for every list on the customer overview tab, so all tables read
// as one system: quiet headers, hairline dividers, tinted status pills.

const BORDER = '#EAECF0'
const DIVIDER = '#F2F4F7'
const SURFACE_MUTED = '#F9FAFB'
const TEXT = '#101828'
const TEXT_MUTED = '#667085'

export const LIST_ROW_HEIGHT = 52
const HEADER_HEIGHT = 40
const FOOTER_HEIGHT = 52

export const listGridSx: SxProps<Theme> = {
  border: `1px solid ${BORDER}`,
  borderRadius: '12px',
  overflow: 'hidden',
  color: TEXT,
  fontSize: '13px',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: SURFACE_MUTED,
    borderBottom: `1px solid ${BORDER}`
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: TEXT_MUTED
  },
  '& .MuiDataGrid-columnSeparator': { display: 'none' },
  '& .MuiDataGrid-cell': {
    borderBottom: `1px solid ${DIVIDER}`,
    alignItems: 'center'
  },
  '& .MuiDataGrid-cellContent': { fontSize: '13px' },
  '& .MuiDataGrid-row:last-of-type .MuiDataGrid-cell': { borderBottom: 'none' },
  '& .MuiDataGrid-row:hover': { backgroundColor: SURFACE_MUTED },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
    { outline: 'none' },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${BORDER}`,
    justifyContent: 'flex-end',
    minHeight: FOOTER_HEIGHT,
    px: 1
  },
  '& .MuiPaginationItem-root': {
    fontSize: '13px',
    minWidth: 28,
    height: 28,
    borderRadius: '8px'
  }
}

// Sizes the table to its rows (capped) so short lists don't leave a block of empty space
export const fitTableHeight = (
  rowCount: number,
  {
    rowHeight = LIST_ROW_HEIGHT,
    maxRows = 8,
    footer = false
  }: { rowHeight?: number; maxRows?: number; footer?: boolean } = {}
) => {
  const visibleRows = Math.min(Math.max(rowCount, 3), maxRows)
  return `${
    HEADER_HEIGHT + visibleRows * rowHeight + (footer ? FOOTER_HEIGHT : 0) + 2
  }px`
}

function EmptyOverlay() {
  return (
    <Stack height='100%' alignItems='center' justifyContent='center'>
      <Typography sx={{ fontSize: '13px', color: TEXT_MUTED }}>
        Nothing to show yet
      </Typography>
    </Stack>
  )
}

// Spread onto MUIDataGrid; pass `sx` after it to extend
export const listGridProps = {
  sx: listGridSx,
  rowHeight: LIST_ROW_HEIGHT,
  headerHeight: HEADER_HEIGHT,
  disableSelectionOnClick: true,
  components: { NoRowsOverlay: EmptyOverlay, NoResultsOverlay: EmptyOverlay }
}

export function ListSection({
  title,
  description,
  count,
  actions,
  children
}: {
  title: string
  description?: string
  count?: number
  actions?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <Stack spacing={2}>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='flex-start'
        gap={2}
      >
        <Box>
          <Stack direction='row' alignItems='center' gap={1}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: TEXT }}>
              {title}
            </Typography>
            {count !== undefined && (
              <Box
                component='span'
                sx={{
                  px: 1,
                  py: '1px',
                  borderRadius: '999px',
                  bgcolor: DIVIDER,
                  color: TEXT_MUTED,
                  fontSize: '12px',
                  fontWeight: 500
                }}
              >
                {count}
              </Box>
            )}
          </Stack>
          {description && (
            <Typography sx={{ fontSize: '13px', color: TEXT_MUTED, mt: 0.25 }}>
              {description}
            </Typography>
          )}
        </Box>
        {actions}
      </Stack>
      {children}
    </Stack>
  )
}

export type StatusTone = 'success' | 'warning' | 'error' | 'info' | 'neutral'

const TONES: Record<StatusTone, { bg: string; fg: string; dot: string }> = {
  success: { bg: '#ECFDF3', fg: '#067647', dot: '#17B26A' },
  warning: { bg: '#FFFAEB', fg: '#B54708', dot: '#F79009' },
  error: { bg: '#FEF3F2', fg: '#B42318', dot: '#F04438' },
  info: { bg: '#EFF8FF', fg: '#175CD3', dot: '#2E90FA' },
  neutral: { bg: '#F2F4F7', fg: '#344054', dot: '#98A2B3' }
}

const DEFAULT_STATUS_TONES: Record<string, StatusTone> = {
  ACTIVE: 'success',
  ASSIGNED: 'success',
  AVAILABLE: 'success',
  PROCESSING: 'info',
  PENDING: 'neutral',
  INACTIVE: 'warning',
  REASSIGNING: 'warning',
  EXPIRED: 'neutral',
  FAILED: 'error',
  TERMINATED: 'error',
  CANCELED: 'error',
  CANCELLED: 'error'
}

const toTitleCase = (value: string) =>
  value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())

export function StatusPill({
  status,
  tone
}: {
  status?: string
  tone?: StatusTone
}) {
  if (!status) return <MutedText>—</MutedText>
  const colors =
    TONES[tone ?? DEFAULT_STATUS_TONES[status.toUpperCase()] ?? 'neutral']
  return (
    <Box
      component='span'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: '2px',
        borderRadius: '999px',
        bgcolor: colors.bg,
        color: colors.fg,
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: '18px',
        whiteSpace: 'nowrap'
      }}
    >
      <Box
        component='span'
        sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: colors.dot }}
      />
      {toTitleCase(status)}
    </Box>
  )
}

export function CellText({
  children,
  strong = false
}: {
  children: React.ReactNode
  strong?: boolean
}) {
  return (
    <Typography
      noWrap
      sx={{ fontSize: '13px', color: TEXT, fontWeight: strong ? 500 : 400 }}
    >
      {children}
    </Typography>
  )
}

export function MutedText({ children }: { children: React.ReactNode }) {
  return (
    <Typography noWrap sx={{ fontSize: '13px', color: TEXT_MUTED }}>
      {children}
    </Typography>
  )
}

export function MonoText({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      noWrap
      sx={{
        fontFamily:
          'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
        fontSize: '12px',
        color: TEXT_MUTED
      }}
    >
      {children || '—'}
    </Typography>
  )
}

export interface RowAction {
  label: string
  onClick: () => void
  danger?: boolean
}

export function RowActionsMenu({ actions }: { actions: RowAction[] }) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  return (
    <>
      <IconButton
        size='small'
        aria-label='Row actions'
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ color: TEXT_MUTED }}
      >
        <MoreHoriz fontSize='small' />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          elevation: 0,
          sx: {
            minWidth: 168,
            mt: 0.5,
            border: `1px solid ${BORDER}`,
            borderRadius: '10px',
            boxShadow: '0 4px 12px rgba(16, 24, 40, 0.08)'
          }
        }}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.label}
            onClick={() => {
              setAnchorEl(null)
              action.onClick()
            }}
            sx={{
              fontSize: '13px',
              py: 1,
              color: action.danger ? 'error.main' : TEXT
            }}
          >
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export function UsageBar({ value, label }: { value: number; label: string }) {
  const clamped = Number.isFinite(value) ? Math.min(Math.max(value, 0), 100) : 0
  return (
    <Stack spacing={0.5} width='100%' maxWidth={200}>
      <Box sx={{ height: 6, borderRadius: '999px', bgcolor: DIVIDER }}>
        <Box
          sx={{
            height: '100%',
            width: `${clamped}%`,
            borderRadius: '999px',
            bgcolor: 'primary.main'
          }}
        />
      </Box>
      <Typography sx={{ fontSize: '12px', color: TEXT_MUTED }}>{label}</Typography>
    </Stack>
  )
}

// -1 marks an unlimited catalog
export const formatDataInGB = (value?: number | null) => {
  if (value == null) return '—'
  return Number(value) === -1 ? 'Unlimited' : `${value} GB`
}

export const formatValidityDays = (value?: number | null) =>
  value == null ? '—' : `${value} day${value === 1 ? '' : 's'}`
